import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import QRCodeStyling from "qr-code-styling";
import { defaultCardsColor, normalizeColor } from "../../lib/cardTools";
import {
  buildEmailSignatureHtml,
  buildEmailSignatureText,
  defaultEmailSignatureFields,
  emailSignatureFieldOptions,
  emailSignatureTemplates,
} from "../../lib/emailSignatureTemplates";

const copyRichHtml = async ({ html, text }) => {
  const ClipboardItemConstructor = window.ClipboardItem;

  if (
    typeof ClipboardItemConstructor !== "undefined" &&
    navigator.clipboard?.write
  ) {
    await navigator.clipboard.write([
      new ClipboardItemConstructor({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([text], { type: "text/plain" }),
      }),
    ]);
    return;
  }

  await navigator.clipboard.writeText(html);
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

function EmailSignatureTool({ data, onTrack }) {
  const [templateId, setTemplateId] = useState("minimal");
  const [primaryColor, setPrimaryColor] = useState(
    data.primaryColor || defaultCardsColor,
  );
  const [visibleFields, setVisibleFields] = useState(
    defaultEmailSignatureFields,
  );
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [copyState, setCopyState] = useState("");
  const viewedTrackedRef = useRef(false);
  const normalizedColor = useMemo(
    () => normalizeColor(primaryColor),
    [primaryColor],
  );
  const signatureHtml = useMemo(
    () =>
      buildEmailSignatureHtml({
        data: {
          ...data,
          qrCodeDataUrl,
        },
        templateId,
        primaryColor: normalizedColor,
        visibleFields,
      }),
    [data, qrCodeDataUrl, templateId, normalizedColor, visibleFields],
  );
  const signatureText = useMemo(
    () => buildEmailSignatureText(data, visibleFields),
    [data, visibleFields],
  );
  const qrEnabled = visibleFields.includes("qrCode");

  useEffect(() => {
    if (viewedTrackedRef.current) return;
    viewedTrackedRef.current = true;
    onTrack?.("tool_signature_viewed", {
      templateId,
      qrEnabled,
      visibleFields,
      visibleFieldsCount: visibleFields.length,
    });
  }, [onTrack, qrEnabled, templateId, visibleFields]);

  useEffect(() => {
    setPrimaryColor(data.primaryColor || defaultCardsColor);
  }, [data.primaryColor]);

  useEffect(() => {
    let ignore = false;

    async function buildQr() {
      if (!data.cardUrl || !qrEnabled) {
        setQrCodeDataUrl("");
        return;
      }

      try {
        const qr = new QRCodeStyling({
          width: 180,
          height: 180,
          data: data.cardUrl,
          margin: 8,
          dotsOptions: {
            color: normalizedColor,
            type: "extra-rounded",
          },
          cornersSquareOptions: {
            color: normalizedColor,
            type: "extra-rounded",
          },
          backgroundOptions: {
            color: "#ffffff",
          },
        });
        const blob = await qr.getRawData("png");
        const dataUrl = await blobToDataUrl(blob);
        if (!ignore) {
          setQrCodeDataUrl(dataUrl);
        }
      } catch {
        if (!ignore) {
          setQrCodeDataUrl("");
        }
      }
    }

    buildQr();

    return () => {
      ignore = true;
    };
  }, [data.cardUrl, normalizedColor, qrEnabled]);

  const toggleField = (fieldName) => {
    setVisibleFields((current) =>
      {
        const enabled = !current.includes(fieldName);
        const next = enabled
          ? [...current, fieldName]
          : current.filter((item) => item !== fieldName);
        onTrack?.("tool_signature_fields_changed", {
          field: fieldName,
          enabled,
          visibleFields: next,
          visibleFieldsCount: next.length,
          qrEnabled: next.includes("qrCode"),
        });
        return next;
      },
    );
  };

  const markCopied = (value) => {
    setCopyState(value);
    window.setTimeout(() => setCopyState(""), 1800);
  };

  const copyHtml = async () => {
    try {
      await copyRichHtml({ html: signatureHtml, text: signatureText });
      markCopied("html");
      onTrack?.("tool_signature_copy_html", {
        templateId,
        qrEnabled,
        visibleFields,
        visibleFieldsCount: visibleFields.length,
      });
    } catch {
      markCopied("error");
      onTrack?.("tool_signature_copy_failed", {
        copyMode: "html",
        templateId,
      });
    }
  };

  const copySource = async () => {
    try {
      await navigator.clipboard.writeText(signatureHtml);
      markCopied("source");
      onTrack?.("tool_signature_copy_source", {
        templateId,
        qrEnabled,
        visibleFields,
        visibleFieldsCount: visibleFields.length,
      });
    } catch {
      markCopied("error");
      onTrack?.("tool_signature_copy_failed", {
        copyMode: "source",
        templateId,
      });
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(signatureText);
      markCopied("text");
      onTrack?.("tool_signature_copy_text", {
        templateId,
        qrEnabled,
        visibleFields,
        visibleFieldsCount: visibleFields.length,
      });
    } catch {
      markCopied("error");
      onTrack?.("tool_signature_copy_failed", {
        copyMode: "text",
        templateId,
      });
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-[minmax(0,0.8fr)_minmax(280px,1fr)] lg:items-start">
      <div className="space-y-4">
        <div>
          <label
            htmlFor="signature-template"
            className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500"
          >
            Template firma
          </label>
          <select
            id="signature-template"
            value={templateId}
            onChange={(event) => {
              setTemplateId(event.target.value);
              onTrack?.("tool_signature_template_changed", {
                templateId: event.target.value,
              });
            }}
            className="mt-2 w-full rounded-md border border-slate-200 bg-white px-3 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-cyan-600 focus:ring-2 focus:ring-cyan-100"
          >
            {emailSignatureTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="signature-primary-color"
            className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500"
          >
            Colore principale
          </label>
          <div className="mt-2 grid grid-cols-[48px_minmax(0,1fr)] overflow-hidden rounded-md border border-slate-200 bg-white focus-within:border-cyan-600 focus-within:ring-2 focus-within:ring-cyan-100">
            <input
              id="signature-primary-color"
              type="color"
              value={normalizedColor}
              onChange={(event) => setPrimaryColor(event.target.value)}
              onBlur={() =>
                onTrack?.("tool_signature_color_changed", {
                  source: "picker",
                })
              }
              className="h-12 w-12 cursor-pointer border-0 bg-transparent p-1"
              aria-label="Colore principale firma email"
            />
            <input
              type="text"
              value={primaryColor}
              onChange={(event) => setPrimaryColor(event.target.value)}
              onBlur={() =>
                onTrack?.("tool_signature_color_changed", {
                  source: "text",
                })
              }
              className="min-w-0 border-0 px-3 py-3 text-sm font-bold text-slate-800 outline-none"
            />
          </div>
        </div>

        <fieldset className="rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
          <legend className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
            Informazioni da mostrare
          </legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {emailSignatureFieldOptions.map(([fieldName, label]) => (
              <label
                key={fieldName}
                className="flex items-center gap-2 rounded-md bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200"
              >
                <input
                  type="checkbox"
                  checked={visibleFields.includes(fieldName)}
                  onChange={() => toggleField(fieldName)}
                  className="h-4 w-4 rounded border-slate-300 text-[#1b8395] focus:ring-cyan-600"
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-2">
          <button
            type="button"
            onClick={copyHtml}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-[#1b8395] px-4 py-3 text-sm font-bold text-white transition hover:bg-[#13526f]"
          >
            <Icon icon={copyState === "html" ? "mdi:check" : "mdi:content-copy"} className="text-lg" />
            {copyState === "html" ? "Firma copiata" : "Copia firma HTML"}
          </button>
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={copyText}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
            >
              <Icon icon={copyState === "text" ? "mdi:check" : "mdi:text-box-outline"} className="text-lg" />
              Solo testo
            </button>
            <button
              type="button"
              onClick={copySource}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
            >
              <Icon icon={copyState === "source" ? "mdi:check" : "mdi:code-tags"} className="text-lg" />
              Codice HTML
            </button>
          </div>
          {copyState === "error" && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-xs font-bold text-red-700">
              Copia non riuscita. Seleziona il codice HTML e copialo manualmente.
            </p>
          )}
        </div>

        <div className="rounded-md bg-cyan-50 px-3 py-3 text-xs font-semibold leading-5 text-[#13526f] ring-1 ring-cyan-100">
          Gmail, Outlook e Apple Mail accettano meglio firme basate su tabelle
          con stili inline. Le immagini devono restare raggiungibili online.
        </div>
        {qrEnabled && (
          <div className="rounded-md bg-amber-50 px-3 py-3 text-xs font-semibold leading-5 text-amber-800 ring-1 ring-amber-200">
            Il QR code viene incorporato come immagine inline nella firma:
            alcuni client email potrebbero rimuoverlo o non salvarlo
            correttamente. Verifica sempre con una mail di prova.
          </div>
        )}
      </div>

      <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200">
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
          Anteprima firma
        </p>
        <div
          className="mt-4 overflow-auto rounded-lg bg-white p-4 ring-1 ring-slate-200"
          dangerouslySetInnerHTML={{ __html: signatureHtml }}
        />
        <details className="mt-3 rounded-md bg-white px-3 py-3 text-xs text-slate-600 ring-1 ring-slate-200">
          <summary className="cursor-pointer font-bold text-slate-700">
            HTML generato
          </summary>
          <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] leading-5">
            {signatureHtml}
          </pre>
        </details>
      </div>
      </div>

      <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-200">
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
          Come usare la firma
        </p>
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 text-xs font-semibold leading-5 text-amber-800">
          La resa della firma può variare in base a client email, versione,
          sistema operativo e impostazioni dell'account. Prima di usarla in modo
          definitivo, invia sempre una mail di prova e verifica che testi, link,
          immagini e spaziature siano corretti.
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          {[
            [
              "Gmail",
              "Copia la firma HTML, apri Impostazioni > Visualizza tutte le impostazioni > Firma, crea o modifica una firma e incolla nel riquadro.",
            ],
            [
              "Outlook",
              "Copia la firma HTML, apri Impostazioni > Account > Firme oppure File > Opzioni > Posta > Firme, poi incolla nell'editor.",
            ],
            [
              "Apple Mail",
              "Copia la firma HTML, apri Mail > Impostazioni > Firme, crea una firma e incolla nell'area di modifica.",
            ],
            [
              "Generico",
              "Se il client non mantiene la formattazione, usa Copia codice HTML oppure Solo testo e incollalo nell'editor firme.",
            ],
          ].map(([title, description]) => (
            <div key={title} className="rounded-md bg-white p-3 ring-1 ring-slate-200">
              <p className="text-sm font-extrabold text-slate-900">{title}</p>
              <p className="mt-1 text-xs font-medium leading-5 text-slate-600">
                {description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default EmailSignatureTool;
